import type { GetArtifactResp, ListArtifactsResp } from "@/types/artifacts.ts";
import type {
  ArtifactAccessResp,
  ClaimedArtifactResp,
  SharedArtifactResp,
  ShareArtifactResp,
} from "@/types/sharedArtifact.ts";
import { queryOptions, skipToken } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";

export const artifactQueries = {
  all: () => ["artifacts"] as const,
  list: (threadId: number, messageId: number) =>
    queryOptions({
      queryKey: [...artifactQueries.all(), threadId, messageId, "list"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ListArtifactsResp>(
          `/api/a/threads/${threadId}/messages/${messageId}/artifacts`,
          { signal },
        );
        return response.data;
      },
    }),
  // TODO(BIG-6187): redundant with the id-only `threadDetail` fetch;
  // consolidate onto it and drop `messageId`.
  detail: (threadId: number, messageId: number, artifactId: string) =>
    queryOptions({
      queryKey: [...artifactQueries.all(), threadId, messageId, artifactId] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<GetArtifactResp>(
          `/api/a/threads/${threadId}/messages/${messageId}/artifacts/${artifactId}`,
          { signal },
        );
        return response.data;
      },
    }),

  /**
   * Who this artifact is shared with by name, plus the applet data setting.
   *
   * Separate from `shareState`: that one answers "is there a public link", and
   * returns null when there isn't — which would make the people list and the
   * applet toggle disappear on an artifact shared only by address.
   */
  access: (artifactId: string | undefined) =>
    queryOptions({
      queryKey: [...artifactQueries.all(), "access", artifactId] as const,
      queryFn: artifactId
        ? async ({ signal }) => {
            const response = await api.get<ArtifactAccessResp>(
              `/api/a/artifacts/${artifactId}/access`,
              { signal },
            );
            return response.data;
          }
        : skipToken,
    }),

  /**
   * Fetch a single artifact by id alone (account-scoped, no thread) — used by the
   * canvas, which addresses artifacts by URN and may show one from an arbitrary
   * thread. Disabled via `skipToken` until an `artifactId` is available, so an
   * always-mounted consumer issues no request when no artifact is targeted.
   */
  byId: (artifactId: string | undefined) =>
    queryOptions({
      queryKey: [...artifactQueries.all(), "byId", artifactId] as const,
      queryFn: artifactId
        ? async ({ signal }) => {
            // A union, because this route serves two shapes: the owner's full
            // artifact, and — for someone reading through a share grant — a
            // redacted payload with no `messageId` or `threadId`. Narrow on
            // `isOwner === false` before reaching for either.
            const response = await api.get<GetArtifactResp | ClaimedArtifactResp>(
              `/api/a/artifacts/${artifactId}`,
              { signal },
            );
            return response.data;
          }
        : skipToken,
    }),

  /**
   * Fetch an artifact through a public share token.
   *
   * `/api/p/` runs no auth middleware, so this resolves for a signed-out
   * visitor.
   *
   * No `retry` override: the shared client's `shouldRetryRequest` already stops
   * on any status below 500 (bar 429), so 404 and 410 — the two answers this
   * view branches on — are terminal already. Overriding it would only give up
   * on the transient failures worth retrying, which for a signed-out visitor
   * with one link and no navigation is the whole page.
   */
  bySharedToken: (shareToken: string | undefined) =>
    queryOptions({
      queryKey: [...artifactQueries.all(), "shared", shareToken] as const,
      queryFn: shareToken
        ? async ({ signal }) => {
            const response = await api.get<SharedArtifactResp>(`/api/p/shared/a/${shareToken}`, {
              signal,
            });
            return response.data;
          }
        : skipToken,
    }),

  /** Current share state for an artifact the caller owns; `null` when unshared. */
  shareState: (artifactId: string | undefined) =>
    queryOptions({
      queryKey: [...artifactQueries.all(), "share", artifactId] as const,
      queryFn: artifactId
        ? async ({ signal }) => {
            const response = await api.get<ShareArtifactResp | null>(
              `/api/a/artifacts/${artifactId}/share`,
              { signal },
            );
            return response.data;
          }
        : skipToken,
    }),
};

export type ArtifactQueries = Record<
  keyof typeof artifactQueries,
  ReturnType<(typeof artifactQueries)[keyof typeof artifactQueries]>
>;
